'use client';

import { useSortedMembers } from '@/app/(intakeFormV2)/intake-v2/hooks/useSortedMembers';
import { calculatePlanPricing } from '@/app/(intakeFormV2)/intake-v2/services/QuoteService';
import { Member, Plan } from '@/app/(intakeFormV2)/intake-v2/types';
import { SalesforcePlanPricingResponse } from '@/types/salesforce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntakeWizardContext } from '../../../contexts/WizardProvider';
import { useWizardNavigation } from '../../../hooks/useWizardNavigation';

export const useQuoteEditData = () => {
  const { state, dispatch } = useIntakeWizardContext();
  const { goToStep } = useWizardNavigation();
  const [pricing, setPricing] = useState<SalesforcePlanPricingResponse[]>([]);
  const [isEditingPlan, setIsEditingPlan] = useState(false);

  const members = useMemo(
    () => state.formData.members || [],
    [state.formData.members]
  );

  const sortedMembers = useSortedMembers(members);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const result = await calculatePlanPricing(state.formData);
        setPricing(result);
      } catch (error) {
        console.error('Error fetching quote:', error);
      }
    };

    fetchQuote();
  }, [state.formData]);

  const savePlanChanges = useCallback(
    (member: Member, plan: Plan) => {
      const updatedMembers = members.map(m =>
        m.id === member.id
          ? { ...m, plan, pediatrics_enabled: plan === 'Pediatrics' }
          : m
      );

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          members: updatedMembers,
          additional_members: updatedMembers.length > 1,
          number_of_members: updatedMembers.length,
        },
      });
    },
    [members, dispatch]
  );

  const handleAddMembers = useCallback(() => {
    goToStep('quote-members');
  }, [goToStep]);

  const handleQuote = useCallback(() => {
    goToStep('quote');
  }, [goToStep]);

  return {
    members: sortedMembers,
    pricing,
    isEditingPlan,
    setIsEditingPlan,
    locationHasPediatrics: state.formData.location_has_pediatrics ?? false,
    savePlanChanges,
    handleAddMembers,
    handleQuote,
  };
};
